import React, { useState } from 'react';
import { 
  Sparkles, 
  Upload, 
  BookOpen, 
  Save, 
  Download, 
  RefreshCw,
  Settings,
  Copy,
  Share2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import ApiService from '../../services/apiService';
import { toast } from 'react-toastify';

const GenerateContentPage = () => {
  const [topic, setTopic] = useState('');
  const [grade, setGrade] = useState('');
  const [subject, setSubject] = useState('');
  const [contentType, setContentType] = useState('');
  const [language, setLanguage] = useState('English');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generationHistory, setGenerationHistory] = useState([]);

  const apiService = new ApiService();

  const handleGenerate = async () => {
    if (!topic || !grade || !subject) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      let response;
      
      // Choose API based on content type
      switch (contentType) {
        case 'worksheet':
          // Use vidya-agent worksheet generation - POST with concept, grades, language
          response = await apiService.makeApiCall('vidya', '/api/generate-worksheet', {
            concept: topic,
            grades: [grade],
            language: language
          }, 'POST');
          if (response.status === 'success') {
            setGeneratedContent(JSON.stringify(response.data.worksheetData, null, 2));
          }
          break;
          
        case 'quiz':
          // Use vidyarthi-agent quiz generation - GET with query params
          const quizParams = new URLSearchParams({
            subject: subject,
            grade: grade,
            questionCount: '5'
          });
          response = await apiService.makeApiCall('vidyarthi', `/api/generate-quiz?${quizParams}`, null, 'GET');
          if (response.status === 'success') {
            setGeneratedContent(`Quiz: ${response.data.topic}\nGrade: ${response.data.grade}\nQuestions: ${response.data.totalQuestions}\n\n${JSON.stringify(response.data.questions, null, 2)}`);
          }
          break;
          
        case 'lesson':
          // Use shikshak-agent lesson plan generation - GET with query params
          const weekString = `${new Date().toLocaleDateString()} - ${new Date(Date.now() + 7*24*60*60*1000).toLocaleDateString()}`;
          const lessonParams = new URLSearchParams({
            week: weekString,
            grades: grade,
            subject: subject,
            language: language
          });
          response = await apiService.makeApiCall('shikshak', `/api/lesson-plan?${lessonParams}`, null, 'GET');
          if (response.status === 'success') {
            setGeneratedContent(JSON.stringify(response.data, null, 2));
          }
          break;
          
        default:
          // Use vidya-agent content generation - POST with concept, gradeLevel, language
          response = await apiService.makeApiCall('vidya', '/api/generate-content', {
            concept: topic,
            gradeLevel: grade,
            language: language
          }, 'POST');
          if (response.status === 'success') {
            setGeneratedContent(response.data.story || response.data.content || 'Content generated successfully');
          }
          break;
      }

      if (response && response.status === 'success') {
        const historyItem = {
          id: Date.now(),
          topic,
          grade,
          subject,
          contentType,
          language,
          timestamp: new Date().toISOString(),
          preview: generatedContent.substring(0, 100) + '...'
        };
        setGenerationHistory(prev => [historyItem, ...prev.slice(0, 4)]);
        toast.success('Content generated successfully!');
      } else {
        throw new Error(response?.message || 'Failed to generate content');
      }
    } catch (err) {
      console.error('Content generation failed:', err);
      setError(err.message);
      toast.error(`Failed to generate content: ${err.message}`);
      
      // Fallback content
      setGeneratedContent(`Generated educational content for "${topic}" - Grade ${grade} ${subject}

Learning Objectives:
- Students will understand the basic concepts of ${topic}
- Students will be able to explain key principles
- Students will apply knowledge through practical examples

Content Overview:
This is ${contentType || 'educational content'} designed for Grade ${grade} students studying ${subject}. The content covers fundamental concepts of ${topic} in an age-appropriate manner with engaging examples and activities.

Key Points:
1. Introduction to ${topic}
2. Main concepts and principles
3. Real-world applications
4. Practice activities and assessments

Note: This is fallback content. For AI-generated content, ensure the API agents are running.`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex flex-row gap-8">
          {/* Main Content Creation Area (2/3 width) */}
          <div className="w-full lg:w-2/3 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-blue-600" />
            Generate Educational Content
          </h1>

          {/* Input Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Enter topic (e.g., Photosynthesis)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grade Level</label>
              <select
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Grade</option>
                <option value="1">Grade 1</option>
                <option value="2">Grade 2</option>
                <option value="3">Grade 3</option>
                <option value="4">Grade 4</option>
                <option value="5">Grade 5</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Subject</option>
                <option value="Science">Science</option>
                <option value="Math">Mathematics</option>
                <option value="English">English</option>
                <option value="History">History</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Type</option>
                <option value="lesson">Lesson Plan</option>
                <option value="worksheet">Worksheet</option>
                <option value="quiz">Quiz</option>
                <option value="explanation">Explanation</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="English">English</option>
                <option value="Hindi">Hindi</option>
                <option value="Marathi">Marathi</option>
                <option value="Tamil">Tamil</option>
                <option value="Telugu">Telugu</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={handleGenerate}
              disabled={!topic || !grade || !subject || isGenerating}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
            >
              {isGenerating ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {isGenerating ? 'Generating...' : 'Generate Content'}
            </button>
            
            <button className="border border-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Advanced Options
            </button>
          </div>

          {/* Generated Content Display */}
          {generatedContent && (
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Generated Content</h3>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => navigator.clipboard.writeText(generatedContent)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded">
                    <Download className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded">
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-700">{generatedContent}</pre>
              </div>
            </div>
          )}
            </div>
          </div>

          {/* Assistant Panel (1/3 width) */}
          <div className="hidden lg:block lg:w-1/3">
            <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-6">
              <h3 className="font-bold text-gray-900 mb-4">Content Assistant</h3>
              
              <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Quick Tips</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Be specific with your topic</li>
                <li>• Consider your students' level</li>
                <li>• Include learning objectives</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">Generation History</h4>
              <div className="space-y-2">
                {generationHistory.length > 0 ? (
                  generationHistory.map((item) => (
                    <button 
                      key={item.id}
                      onClick={() => {
                        setTopic(item.topic);
                        setGrade(item.grade);
                        setSubject(item.subject);
                        setContentType(item.contentType);
                        setLanguage(item.language);
                      }}
                      className="block w-full text-left text-sm text-green-700 hover:text-green-900 p-2 hover:bg-green-100 rounded"
                    >
                      {item.topic} - Grade {item.grade}
                      <div className="text-xs text-green-600">{item.contentType} • {item.language}</div>
                    </button>
                  ))
                ) : (
                  <div className="text-sm text-green-600">No recent generations</div>
                )}
              </div>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-2">Templates</h4>
              <div className="space-y-2">
                <button className="flex items-center gap-2 w-full text-left text-sm text-yellow-700 hover:text-yellow-900 p-2 hover:bg-yellow-100 rounded">
                  <BookOpen className="w-4 h-4" />
                  Lesson Plan Template
                </button>
                <button className="flex items-center gap-2 w-full text-left text-sm text-yellow-700 hover:text-yellow-900 p-2 hover:bg-yellow-100 rounded">
                  <Upload className="w-4 h-4" />
                  Upload Custom Template
                </button>
              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateContentPage;